// ─────────────────────────────────────────────────────────────────────────────
//  graphs/LineChart.jsx
//
//  Self-contained:  transform  •  tooltips  •  legend  •  render
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import ReactApexChart from "react-apexcharts";
import {
    ChartAnimationWrapper, ErrorMessage,
    getBaseChartConfig, getSeriesColors, getSingleUnit,
    formatNumberWithSuffix, formatYAxisSmart, sanitiseSeriesName,
    formatSeries, detectUnitField,
    BIOMEDICAL_WASTE_COLORS, DEFAULT_COLORS,
    axisLabelStyle, monoLabelStyle,
} from "../mainUnit";

// ─── TRANSFORM ────────────────────────────────────────────────────────────────
const transformData = (rawData, groupedField, title, dataSourceType) => {
    if (!rawData || rawData.length === 0) return null;

    const xAxisField   = groupedField?.[0];
    const stackByField = groupedField?.[1] || null;
    let series = [], categories = [], colorMap = {}, unitsMap = {}, showCategoryLegend = true;

    if (stackByField && xAxisField) {
        const groupedData = {}, stackValues = new Set();
        rawData.forEach(item => {
            const xVal     = item[xAxisField];
            const stackVal = item[stackByField];
            const value    = parseFloat(item.sum_value || item.min_value) || 0;
            stackValues.add(stackVal);
            if (!groupedData[stackVal]) groupedData[stackVal] = {};
            groupedData[stackVal][xVal] = (groupedData[stackVal][xVal] || 0) + value;
        });

        const totals = {};
        rawData.forEach(item => {
            const xVal  = item[xAxisField];
            const value = parseFloat(item.sum_value || item.min_value) || 0;
            totals[xVal] = (totals[xVal] || 0) + value;
        });
        categories = Object.keys(totals);

        // ★ FIX: capture which field owns the unit.
        //   unitOnStackBy=true  → series[].unit set correctly per stack value
        //   unitOnStackBy=false → series[].unit must stay null; tooltip reads
        //                         unitsMap[categoryLabel] at hover time instead
        const { unitField: detectedUnitField, unitsMap: detectedUnitsMap } = detectUnitField(rawData, xAxisField, stackByField);
        unitsMap = detectedUnitsMap;
        const unitOnStackBy = detectedUnitField === stackByField;

        const stackByUnitLookup = {};
        rawData.forEach(item => {
            if (!item.unit || !stackByField) return;
            stackByUnitLookup[String(item[stackByField] ?? '')] = item.unit;
        });

        [...stackValues].forEach((stackVal, index) => {
            const color = BIOMEDICAL_WASTE_COLORS[stackVal] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];
            colorMap[stackVal] = color;
            const unit = unitOnStackBy ? (stackByUnitLookup[stackVal] ?? null) : null;
            series.push({ name: String(stackVal), unit, data: categories.map(xVal => groupedData[stackVal][xVal] || 0), color });
        });
    } else if (xAxisField) {
        showCategoryLegend = true;
        const aggregated = {};
        rawData.forEach(item => {
            const label = item[xAxisField] || 'Unknown';
            aggregated[label] = (aggregated[label] || 0) + (parseFloat(item.sum_value || item.min_value) || 0);
            if (item.unit) unitsMap[label] = item.unit;
        });
        const labels = Object.keys(aggregated);
        labels.forEach((lbl, idx) => { colorMap[lbl] = BIOMEDICAL_WASTE_COLORS[lbl] || DEFAULT_COLORS[idx % DEFAULT_COLORS.length]; });
        series = [{ data: labels.map(lbl => ({ x: lbl, y: aggregated[lbl], fillColor: colorMap[lbl] })) }];
    }

    return {
        graphType: 'line', dataSourceType, title: title || 'Analysis',
        categories, series: formatSeries(series),
        colors: DEFAULT_COLORS, colorMap, unitsMap,
        stacked: !!stackByField, showCategoryLegend,
    };
};

// ─── TOOLTIP: distributed (single-series, one colour per point) ───────────────
const buildDistributedTooltip = (categoriesArr, distColors, unitsMap, sharedUnit) => {
    return ({ series, seriesIndex, dataPointIndex, w }) => {
        const dp     = w.config.series[0]?.data?.[dataPointIndex];
        const label  = (dp && typeof dp === 'object' ? dp.x : null)
            ?? (categoriesArr || [])[dataPointIndex] ?? `Point ${dataPointIndex + 1}`;
        const rawVal = series[seriesIndex]?.[dataPointIndex]
            ?? (dp && typeof dp === 'object' ? dp.y : dp) ?? 0;
        const numVal = typeof rawVal === 'number' ? rawVal : (parseFloat(rawVal) || 0);
        const color  = (w.config.colors || distColors)[dataPointIndex % (distColors?.length || 1)];
        const unit   = sharedUnit || unitsMap?.[label] || '';
        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:160px;max-width:280px;">
                <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
                    <div style="width:10px;height:10px;border-radius:50%;background:${color};flex-shrink:0;margin-top:3px;"></div>
                    <span style="font-size:12.5px;font-weight:700;color:#0f172a;line-height:1.4;word-break:break-word;">${label}</span>
                </div>
                <div style="font-size:20px;font-weight:900;color:#0f172a;font-family:'DM Mono',monospace;letter-spacing:-0.02em;line-height:1;">
                    ${formatNumberWithSuffix(numVal)}
                    ${unit ? `<span style="font-size:11px;font-weight:500;color:#94a3b8;margin-left:5px;">${unit}</span>` : ''}
                </div>
            </div>`;
    };
};

// ─────────────────────────────────────────────────────────────────────────────
//  TOOLTIP: multi-series — handles BOTH unit-on-xAxis and unit-on-stackBy
//
//  Case A — unit on stackByField  →  series[].unit set, seriesUnitMap populated
//    categoryUnit = '' (unitsMap keyed by stack values, not x-axis categories)
//    rowUnit = '' || seriesUnitMap[name]  →  correct per-series unit           ✓
//
//  Case B — unit on xAxisField  →  series[].unit = null, seriesUnitMap empty
//    categoryUnit = unitsMap[categoryLabel]  (e.g. 'PNG' → 'SCM')
//    rowUnit = categoryUnit  →  correct per point group                        ✓
//
//  Rule: categoryUnit wins when non-empty (x-axis keyed, always correct).
//        seriesUnitMap only consulted when categoryUnit is empty (Case A).
// ─────────────────────────────────────────────────────────────────────────────
const buildStackedTooltip = (categoriesArr, seriesColors, seriesArr, sharedUnit, unitsMap) => {
    const seriesUnitMap = {};
    (seriesArr || []).forEach(s => {
        if (s.unit) {
            seriesUnitMap[String(s.name)]                     = s.unit;
            seriesUnitMap[sanitiseSeriesName(String(s.name))] = s.unit;
        }
    });

    return ({ series, dataPointIndex, w }) => {
        const categoryLabel = (categoriesArr || [])[dataPointIndex] ?? `#${dataPointIndex}`;
        const categoryUnit  = unitsMap?.[categoryLabel] || '';

        const rows = (w.config.series || []).map((s, si) => {
            const val   = series[si]?.[dataPointIndex];
            const color = (w.config.colors || seriesColors)[si] || '#94a3b8';
            const name  = sanitiseSeriesName(s.name);
            if (val === null || val === undefined || val === 0) return '';

            const rowUnit = categoryUnit || seriesUnitMap[s.name] || seriesUnitMap[name] || '';

            // Show unit badge on label only for Case A (per-series units differ)
            const labelSuffix = (!categoryUnit && rowUnit)
                ? ` <span style="font-size:10px;color:#94a3b8;">(${rowUnit})</span>`
                : '';

            return `
                <div style="display:flex;align-items:center;gap:8px;padding:2px 0;">
                    <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
                    <span style="font-size:12px;color:#475569;flex:1;">${name}${labelSuffix}:</span>
                    <span style="font-size:12px;font-weight:700;color:#0f172a;font-family:'DM Mono',monospace;margin-left:8px;">
                        ${formatNumberWithSuffix(val)}
                        ${rowUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:3px;">${rowUnit}</span>` : ''}
                    </span>
                </div>`;
        }).filter(Boolean).join('');

        // Case B: categoryUnit varies per point — always show it in header
        // Case A: categoryUnit='' — show shared series unit if all same, else ''
        const headerUnit = sharedUnit || categoryUnit || (() => {
            const all = [...new Set(
                (w.config.series || []).map(s => {
                    const n = sanitiseSeriesName(s.name);
                    return seriesUnitMap[s.name] || seriesUnitMap[n] || '';
                }).filter(Boolean)
            )];
            return all.length === 1 ? all[0] : '';
        })();

        return `
            <div style="padding:10px 14px;background:#fff;border:1px solid #e2e8f0;border-radius:10px;
                box-shadow:0 4px 16px rgba(15,23,42,0.1);font-family:'DM Sans',system-ui,sans-serif;
                min-width:180px;max-width:300px;">
                <div style="font-size:12.5px;font-weight:700;color:#0f172a;margin-bottom:8px;
                    padding-bottom:6px;border-bottom:1px solid #f1f5f9;">
                    ${categoryLabel}
                    ${headerUnit ? `<span style="font-size:10px;font-weight:500;color:#94a3b8;margin-left:5px;">(${headerUnit})</span>` : ''}
                </div>
                ${rows || '<div style="font-size:11px;color:#94a3b8;">No data</div>'}
            </div>`;
    };
};

// ─── LEGEND FORMATTERS ────────────────────────────────────────────────────────
const buildDistributedLegend = (baseConfig, categories, distColors, colorMap) => ({
    ...baseConfig.legend,
    customLegendItems: categories?.length ? categories : Object.keys(colorMap || {}),
    markers: { fillColors: categories?.length ? distColors : Object.values(colorMap || {}), width: 10, height: 10, radius: 3 },
    formatter: (name) => sanitiseSeriesName(name),
});

const buildSeriesLegend = (baseConfig, seriesArr, sharedUnit) => ({
    ...baseConfig.legend,
    formatter: (seriesName) => {
        const clean = sanitiseSeriesName(seriesName);
        if (sharedUnit) return clean;
        const s = (seriesArr || []).find(x => x.name === seriesName);
        return s?.unit ? `${clean} (${s.unit})` : clean;
    },
});

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export const LineChart = ({ chartData, chartOptions = {}, groupedField = [] }) => {
    const data = chartOptions.transform
        ? transformData(chartData?.data, groupedField, chartData?.title, chartData?.dataSourceType)
        : chartData;

    if (data?.errorMessage) return <ErrorMessage message={data.errorMessage} />;
    if (!data?.series?.length) return <ErrorMessage />;

    const toolbarVisible = chartOptions.toolbarVisible !== false;
    const isStacked      = !!data.stacked;
    const seriesColors   = getSeriesColors(data.series, data.colorMap, data.colors);
    const sharedUnit     = getSingleUnit(data.unitsMap);
    const baseConfig     = getBaseChartConfig('light', seriesColors, data, toolbarVisible);

    // ★ FIX: mirror BarChart's showCategoryLegend && !isStacked pattern
    //   (previously used data.series?.length === 1 which breaks when a single
    //    grouped series happens to have only one stack member)
    const showCategoryLegend = data.showCategoryLegend && !isStacked;
    const distColors         = showCategoryLegend && data.colorMap
        ? Object.values(data.colorMap)
        : seriesColors;

    // ★ FIX: same hasMixedUnits pattern as BarChart — units on xAxisField exist
    //   regardless of stacking, so we must check both sources.
    const hasSeriesUnits   = data.series.some(s => !!s.unit);
    const hasCategoryUnits = !sharedUnit && data.unitsMap && Object.keys(data.unitsMap).length > 0;
    const hasMixedUnits    = !sharedUnit && (hasSeriesUnits || hasCategoryUnits);

    // Tooltip selection — mirrors BarChart exactly:
    //   showCategoryLegend → distributed (one colour per point)
    //   hasMixedUnits      → multi-series with per-unit resolution
    //   otherwise          → ApexCharts default
    const customTooltip = showCategoryLegend
        ? buildDistributedTooltip(data.categories, distColors, data.unitsMap, sharedUnit)
        : hasMixedUnits
            ? buildStackedTooltip(data.categories, seriesColors, data.series, sharedUnit, data.unitsMap)
            : undefined;

    const legendConfig = showCategoryLegend
        ? buildDistributedLegend(baseConfig, data.categories, distColors, data.colorMap)
        : buildSeriesLegend(baseConfig, data.series, sharedUnit);

    const chartConfig = {
        ...baseConfig,
        chart:      { ...baseConfig.chart, type: 'line', height: chartOptions.height || 450 },
        stroke:     { curve: chartOptions.curve || 'smooth', width: chartOptions.strokeWidth || 2.5 },
        xaxis:      {
            categories: data.categories || [],
            labels: { rotate: -38, style: axisLabelStyle },
            axisBorder: { show: true, color: '#94a3b8', height: 1 },
            axisTicks:  { show: true, color: '#94a3b8', height: 6 },
        },
        yaxis:      {
            labels: { formatter: formatYAxisSmart, style: monoLabelStyle },
            axisBorder: { show: true, color: '#94a3b8', width: 1, offsetX: 0 },
            axisTicks:  { show: true, color: '#94a3b8', width: 6, offsetX: 0 },
        },
        markers:    { size: chartOptions.markerSize || 4, strokeWidth: 3, strokeColors: '#fff', fillOpacity: 1, hover: { size: 7, sizeOffset: 2 } },
        dataLabels: { enabled: false },
        tooltip:    {
            ...baseConfig.tooltip,
            shared: !showCategoryLegend,
            intersect: showCategoryLegend,
            ...(customTooltip ? { custom: customTooltip } : {}),
        },
        legend:     legendConfig,
    };

    return (
        <ChartAnimationWrapper variant="fadeLeft" duration={650}>
            <div style={{ overflowX: 'auto' }}>
                <ReactApexChart options={chartConfig} series={data.series} type="line" height={chartOptions.height || 450} />
            </div>
        </ChartAnimationWrapper>
    );
};